import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchMySignups } from "../../utility/submitSignup";
import {
  bowlingCenterMapsHref,
  eventEntryFee,
  signupAdvertisedTotal,
} from "../../utility/signupDashboard";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import {
  AlertBox,
  Badge,
  SecondaryButton,
  formatCurrency,
  formatSquadInfo,
} from "../UI/DesignStandards";
import { SignupEditSkeleton } from "./SignupEdit/index";
import AverageProofViewer from "./AverageProofViewer";
import {
  deleteSignupAverageProof,
  uploadSignupAverageProof,
} from "../../utility/signupAverageProof";
import { replaceSignupAverageProof } from "../../utility/signupRpc";
import { tournamentPath } from "../../utility/tournamentUrls";

const paymentBadge = (signup) => {
  if (signup.status === "waitlisted") {
    return {
      label: signup.waitlistPosition
        ? `Waitlist · position ${signup.waitlistPosition}`
        : "Waitlist",
      variant: "warning",
    };
  }
  if (signup.status === "cancelled") {
    return { label: "Cancelled", variant: "default" };
  }
  if (signup.status === "denied") {
    return { label: "Denied", variant: "default" };
  }
  if (signup.paymentStatus === "paid" || signup.paymentStatus === "walk_up") {
    return { label: "Paid", variant: "success" };
  }
  if (signup.paymentStatus === "held") {
    return { label: "Held", variant: "default" };
  }
  if (signup.paymentStatus === "refunded") {
    return { label: "Refunded", variant: "default" };
  }
  return { label: "Due", variant: "primary" };
};

const MetaRow = ({ label, children }) => (
  <div className="grid grid-cols-[6.5rem_1fr] sm:grid-cols-[7.5rem_1fr] gap-x-3 text-sm">
    <dt className="text-brand-muted">{label}</dt>
    <dd className="text-brand-charcoal min-w-0 break-all">{children}</dd>
  </div>
);

const AmountRow = ({ label, amount, emphasize = false }) => (
  <div
    className={`flex justify-between items-baseline gap-4 ${
      emphasize
        ? "font-display font-bold text-base mt-3 pt-3 border-t border-brand-charcoal/10"
        : "text-sm text-brand-charcoal"
    }`}
  >
    <span className={emphasize ? "" : "text-brand-muted"}>{label}</span>
    <span className="font-score tabular-nums shrink-0">
      {formatCurrency(amount)}
    </span>
  </div>
);

const bowlerRoleLabel = (bowler, index) => {
  if (bowler.role === "primary" || index === 0) return "Primary bowler";
  return "Teammate";
};

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-brand-cream">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex justify-center">
        <Breadcrumb
          items={[
            { label: "My Signups", path: "/signup/dashboard" },
            { label: "Signup details" },
          ]}
        />
      </div>
      {children}
    </div>
  </div>
);

const SignupEdit = () => {
  const { signupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const proofInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signup, setSignup] = useState(null);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofPath, setProofPath] = useState("");
  const [proofError, setProofError] = useState(null);
  const [proofSaving, setProofSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!signupId) {
        setError("Signup not found.");
        setSignup(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchMySignups();
        if (cancelled) return;
        const found = (data.signups || []).find((row) => row.id === signupId);
        if (!found) {
          setSignup(null);
          setError("Signup not found.");
          return;
        }
        setSignup(found);
      } catch (err) {
        if (cancelled) return;
        setSignup(null);
        setError(err.message || "Could not load this signup.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [signupId]);

  useEffect(() => {
    setProofPath(signup?.averageProofPath || "");
    setProofError(null);
  }, [signup?.id, signup?.averageProofPath]);

  const handleProofFile = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file || !signup || !user) return;
    setProofSaving(true);
    setProofError(null);
    try {
      const path = await uploadSignupAverageProof({
        tournamentId: signup.tournament?.id,
        userId: user.id,
        file,
      });
      const result = await replaceSignupAverageProof({
        signupId: signup.id,
        path,
      });
      if (result?.previousPath && result.previousPath !== path) {
        await deleteSignupAverageProof(result.previousPath);
      }
      setProofPath(result?.averageProofPath || path);
    } catch (proofSaveError) {
      setProofError(
        proofSaveError.message || "Could not save average verification."
      );
    } finally {
      setProofSaving(false);
    }
  };

  const goToMySignups = () => navigate("/signup/dashboard");

  if (loading) {
    return <SignupEditSkeleton />;
  }

  if (error || !signup) {
    return (
      <PageShell>
        <AlertBox
          type="error"
          title="Please fix this"
          message={error || "Signup not found."}
        />
        <SecondaryButton onClick={goToMySignups}>
          Back to My Signups
        </SecondaryButton>
      </PageShell>
    );
  }

  const status = paymentBadge(signup);
  const bowlingCenter = signup.squad?.bowlingCenters?.centerName;
  const mapsHref = bowlingCenterMapsHref(signup.squad?.bowlingCenters);
  const tournamentId = signup.tournament?.id;
  const tournamentSlug = signup.tournament?.slug || tournamentId;
  const tournamentName = signup.tournament?.name || "Tournament";
  const bowlers = signup.signupBowlers || signup.bowlers || [];
  const showHoldEnds =
    Boolean(signup.heldUntil) &&
    (signup.status === "held" || signup.paymentStatus === "held");
  const entryTotal = eventEntryFee(signup.tournamentEvents) * Math.max(bowlers.length, 1);
  const sidepotTotal = bowlers.reduce(
    (sum, bowler) => sum + Number(bowler.totalSidepotCost || 0),
    0
  );
  const bracketTotal = bowlers.reduce(
    (sum, bowler) => sum + Number(bowler.totalBracketCost || 0),
    0
  );

  return (
    <PageShell>
      <div className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-brand-mist">
                {tournamentId ? (
                  <Link
                    to={tournamentPath(tournamentSlug)}
                    className="text-sm text-brand-gold hover:underline"
                  >
                    {tournamentName}
                  </Link>
                ) : (
                  tournamentName
                )}
              </p>
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                {signup.tournamentEvents?.eventName || "Entry"}
              </h1>
            </div>
            <div className="font-score text-lg font-display font-bold text-brand-cream tabular-nums shrink-0">
              {formatCurrency(signupAdvertisedTotal(signup))}
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-8">
          <div className="flex flex-wrap gap-2">
            <Badge variant={status.variant}>{status.label}</Badge>
            {signup.refundRequested ? (
              <Badge variant="warning">Refund requested</Badge>
            ) : null}
            {signup.disputed ? (
              <Badge variant="danger">Disputed</Badge>
            ) : null}
          </div>

          <dl className="space-y-1.5">
            <MetaRow label="Squad">
              {formatSquadInfo(signup.squad?.squadDate, signup.squad?.squadTime)}
            </MetaRow>
            {bowlingCenter ? (
              <MetaRow label="Bowling center">
                {mapsHref ? (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-gold-ink hover:underline"
                  >
                    {bowlingCenter}
                  </a>
                ) : (
                  bowlingCenter
                )}
              </MetaRow>
            ) : null}
            {showHoldEnds ? (
              <MetaRow label="Hold ends">
                {new Date(signup.heldUntil).toLocaleString()}
              </MetaRow>
            ) : null}
            {signup.status === "denied" && signup.deniedReason ? (
              <MetaRow label="Denied">{signup.deniedReason}</MetaRow>
            ) : null}
          </dl>

          {bowlers.map((bowler, index) => {
            const sidepots = bowler.sidepots || [];
            const tickets = bowler.bracketTickets || [];
            return (
              <section key={bowler.id || `${bowler.name}-${index}`}>
                <h2 className="font-display font-bold text-brand-charcoal tracking-tight">
                  {bowler.name || "Bowler"}
                </h2>
                <p className="text-sm text-brand-muted mt-0.5">
                  {bowlerRoleLabel(bowler, index)}
                </p>
                <dl className="mt-3 space-y-1.5">
                  {bowler.email ? (
                    <MetaRow label="Email">{bowler.email}</MetaRow>
                  ) : null}
                  {bowler.phone ? (
                    <MetaRow label="Phone">{bowler.phone}</MetaRow>
                  ) : null}
                  {bowler.usbcID ? (
                    <MetaRow label="USBC">{bowler.usbcID}</MetaRow>
                  ) : null}
                  {bowler.average != null && bowler.average !== "" ? (
                    <MetaRow label="Average">{bowler.average}</MetaRow>
                  ) : null}
                </dl>
                {sidepots.length > 0 ? (
                  <div className="mt-4">
                    <h3 className="text-sm text-brand-muted mb-2">Sidepots</h3>
                    <ul className="space-y-1.5">
                      {sidepots.map((sidepot) => (
                        <li
                          key={sidepot.eventID || sidepot.eventName}
                          className="flex justify-between items-baseline gap-4 text-sm text-brand-charcoal"
                        >
                          <span>{sidepot.eventName}</span>
                          <span className="font-score tabular-nums shrink-0">
                            {formatCurrency(sidepot.entryFee)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {tickets.length > 0 ? (
                  <div className="mt-4">
                    <h3 className="text-sm text-brand-muted mb-2">Brackets</h3>
                    <ul className="space-y-1.5">
                      {tickets.map((ticket) => (
                        <li
                          key={ticket.productID || ticket.productName}
                          className="flex justify-between items-baseline gap-4 text-sm text-brand-charcoal"
                        >
                          <span>
                            {ticket.ticketCount} × {ticket.productName}
                          </span>
                          <span className="font-score tabular-nums shrink-0">
                            {formatCurrency(
                              Number(ticket.ticketCount || 0) *
                                Number(ticket.entryAmount || 0)
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>
            );
          })}

          {proofPath || signup.canChangeAverageProof ? (
            <section>
              <h2 className="font-display font-bold text-brand-charcoal tracking-tight">
                Average verification
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {proofPath ? (
                  <SecondaryButton
                    onClick={() => setProofOpen(true)}
                    disabled={proofSaving}
                    className="!px-3 !py-2 text-sm"
                  >
                    Average verification
                  </SecondaryButton>
                ) : null}
                {signup.canChangeAverageProof ? (
                  <SecondaryButton
                    onClick={() => proofInputRef.current?.click()}
                    disabled={proofSaving}
                    loading={proofSaving}
                    className="!px-3 !py-2 text-sm"
                  >
                    {proofSaving
                      ? proofPath
                        ? "Replacing..."
                        : "Uploading..."
                      : proofPath
                      ? "Replace"
                      : "Upload"}
                  </SecondaryButton>
                ) : null}
              </div>
              <input
                ref={proofInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                className="hidden"
                onChange={handleProofFile}
              />
              {proofError ? (
                <p className="mt-2 text-sm text-red-600">{proofError}</p>
              ) : null}
              <AverageProofViewer
                isOpen={proofOpen}
                onClose={() => setProofOpen(false)}
                storagePath={proofPath}
              />
            </section>
          ) : null}

          <section>
            <h2 className="font-display font-bold text-brand-charcoal tracking-tight mb-3">
              Cost
            </h2>
            {entryTotal > 0 ? (
              <AmountRow label="Entry" amount={entryTotal} />
            ) : null}
            {sidepotTotal > 0 ? (
              <AmountRow label="Sidepots" amount={sidepotTotal} />
            ) : null}
            {bracketTotal > 0 ? (
              <AmountRow label="Brackets" amount={bracketTotal} />
            ) : null}
            <AmountRow
              label="Total"
              amount={signupAdvertisedTotal(signup)}
              emphasize
            />
          </section>

          <SecondaryButton onClick={goToMySignups}>
            Back to My Signups
          </SecondaryButton>
        </div>
      </div>
    </PageShell>
  );
};

export default SignupEdit;
